import { z } from 'zod';

import type { MatchesSchema } from '@/shared/api/contract';

/**
 * Tożsamość i organizacja — model publiczny modułu (spec sek. 10).
 *
 * ⚠ `TeamSummary` istnieje też w `features/auth`. To **nie jest** przeoczenie: tam jest
 * fragmentem odpowiedzi o zalogowanym użytkowniku, tu — bytem, którym się zarządza.
 * Oba są związane z **tym samym** schematem OpenAPI (decyzja 74), więc rozjechać się
 * nie mogą; scalanie ich zrobiłoby zależność `auth → identity` tylko dla trzech pól.
 */

/**
 * Lekka referencja do zespołu — **bez licznika członków**.
 *
 * Licznik zmienia się z powodów niezwiązanych z konkretną osobą, więc przy wierszu listy
 * kont wyglądałby jak dana o tej osobie, a nie jest.
 */
export const teamRefSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type TeamRef = z.infer<typeof teamRefSchema>;

/**
 * Lekka referencja do osoby — **bez mejla, roli i stanu konta**.
 *
 * ⭐ Ten kształt istnieje po to, żeby skład zespołu mógł być widoczny dla **każdego
 * zalogowanego** (spec 9.2), podczas gdy `UserSummary` zostaje przy adminie.
 */
export const userRefSchema = z.object({
  id: z.number(),
  displayName: z.string(),
});

export type UserRef = z.infer<typeof userRefSchema>;

/** Byt organizacyjny — user albo zespół — w jednym worku (spec sek. 3). */
export const orgRefSchema = z.object({
  kind: z.enum(['user', 'team']),
  id: z.number(),
  name: z.string(),
});

export type OrgRef = z.infer<typeof orgRefSchema>;

/**
 * Stan konta — **trzy stany, nie dwa** (słowo usera 2026-09-04).
 *
 * ⚠ „Czeka na wpuszczenie" i „admin zablokował" to nie to samo, choć oba znaczą „nie zaloguje
 * się": pierwsze czeka na decyzję, drugie **jest** decyzją. Import traktuje je inaczej, a
 * interfejs pisze przy nich co innego — jedna flaga nie umie powiedzieć obu rzeczy.
 */
export const userAccountStateSchema = z.enum(['pending', 'active', 'disabled']);

export type UserAccountState = z.infer<typeof userAccountStateSchema>;

export const userSummarySchema = z.object({
  id: z.number(),
  /** Mejl pracowniczy — jednocześnie login (spec sek. 10). */
  login: z.string(),
  displayName: z.string(),
  /**
   * ⭐ Konto czekające to **stan normalny, nie awaria**: rejestracja z sekretnego linku zakłada
   * konto, a admin je wpuszcza — i to jest cała weryfikacja tożsamości w produkcie (spec sek. 10).
   * Lista użytkowników jest więc **i kolejką, i kartoteką**.
   *
   * ⚠ Jedyne pole o tym, czy konto żyje. Do 2026-09-04 stały tu obok siebie `isActive`
   * i `neverActivated`, czyli dwa pola na trzy stany — a front składał je inaczej w każdym miejscu.
   */
  state: userAccountStateSchema,
  roles: z.array(z.string()),
  createdAtUtc: z.string(),
  /** W jakich zespołach jest to konto — macierz user↔team jednym wywołaniem. */
  teams: z.array(teamRefSchema),
  /**
   * Czy konto ma hasło — czyli czy „Wpuść" w ogóle ma sens.
   *
   * ⭐ Konto założone importem albo przez `--set-first-admin` czeka na **link aktywacyjny**,
   * nie na przycisk admina: serwer odmówiłby, a kolejka „czekają na wpuszczenie" zapełniłaby
   * się pozycjami, z którymi admin nic nie zrobi.
   */
  hasPassword: z.boolean(),
});

export type UserSummary = z.infer<typeof userSummarySchema>;

/**
 * Strona listy kont.
 *
 * ⚠ Stronicowanie nie jest ozdobą: lista czytała do 2026-09-03 **wszystkie** konta i całe
 * tabele ról oraz członkostw. Przy trzydziestu kontach nieszkodliwe, po imporcie już nie.
 */
export const usersPageSchema = z.object({
  items: z.array(userSummarySchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type UsersPage = z.infer<typeof usersPageSchema>;

export const teamSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  /**
   * **Klucz** — symbol, po którym zespół rozpoznaje plik importu (słowo usera 2026-09-04).
   *
   * ⭐ Osobno od nazwy, bo nazwa jest dla ludzi i wolno ją poprawić, a plik musi wskazywać ten
   * sam zespół także po zmianie nazwy — ta sama zasada, co przy tożsamości elementu (spec 11.2).
   *
   * ⚠ `null` tylko na wierszu sprzed migracji klucza; taki zespół jest z pliku **nieadresowalny**
   * i ekran ma to powiedzieć, zamiast udawać, że klucz jest.
   */
  key: z.string().nullable(),
  memberCount: z.number(),
  /**
   * Za ile elementów zespół odpowiada **wprost**. Mówi adminowi, czym zespół właściwie
   * jest — i jednocześnie zapowiada, czy usunięcie się uda: własność blokuje usunięcie,
   * bo `element_owners` nie ma klucza obcego do zespołów i zostałaby wisząca.
   */
  ownedElementCount: z.number(),
});

export type TeamSummary = z.infer<typeof teamSummarySchema>;

/**
 * Sekretny link firmy — **jedyna przepustka do rejestracji** (spec 4A/10).
 *
 * Jeden link na firmę, wielokrotnego użytku, odnawialny. Odnowienie unieważnia poprzedni,
 * więc jest odpowiednikiem „ktoś wyniósł link" — stąd akcja stoi obok, a nie w menu.
 */
export const registrationLinkSchema = z.object({
  token: z.string(),
  path: z.string(),
  createdAtUtc: z.string(),
});

export type RegistrationLink = z.infer<typeof registrationLinkSchema>;

export interface SetPasswordInput {
  userId: number;
  newPassword: string;
}

export interface TeamMembershipInput {
  teamId: number;
  userId: number;
}

// ── Spinacz z kontraktem API (decyzja 74) ───────────────────────────────────

export const identityContract = {
  UserSummary: true satisfies MatchesSchema<UserSummary, 'UserSummary'>,
  UsersPage: true satisfies MatchesSchema<UsersPage, 'UsersPage'>,
  UserAccountState: true satisfies MatchesSchema<UserAccountState, 'UserAccountState'>,
  UserRef: true satisfies MatchesSchema<UserRef, 'UserRef'>,
  TeamSummary: true satisfies MatchesSchema<TeamSummary, 'TeamSummary'>,
  TeamRef: true satisfies MatchesSchema<TeamRef, 'TeamRef'>,
  OrgRef: true satisfies MatchesSchema<OrgRef, 'OrgRef'>,
  RegistrationLink: true satisfies MatchesSchema<RegistrationLink, 'RegistrationLinkResponse'>,
} as const;
